<template>
    <main class="user-wallet header-margin">
        <div class="wallet-page">
            <section class="wallet-hero">
                <h1>{{ $t('userWallet.title') }}</h1>

                <wallet-balance-section
                    :loading="loadingWallet"
                    :error="walletError"
                    :wallet-info="walletInfo"
                    @receive="openReceiveModal"
                    @stake="openStakeModal"
                    @send="openTransferModal"
                />
            </section>

            <wallet-unstake-section ref="unstakeSection" @claimed="onClaimed" />

            <wallet-history-section ref="historySection" @tab-change="onHistoryTabChange" @activities-loaded="onActivitiesLoaded" />
        </div>

        <wallet-receive-modal
            v-if="showReceiveModal"
            :address="walletAddress"
            @close="showReceiveModal = false"
        />

        <wallet-stake-modal
            v-if="showStakeModal"
            :available-balance="availableBalance"
            :plans="plans"
            @close="showStakeModal = false"
            @accepted="onStakeAccepted"
        />

        <wallet-transfer-modal
            v-if="showTransferModal"
            :symbol="transferSymbol"
            :wallet-info="walletInfo"
            @close="onTransferClosed"
            @refresh-wallet="fetchWallet(false)"
        />

        <wallet-stake-notice-modal
            v-if="showStakeNoticeModal"
            @close="showStakeNoticeModal = false"
        />
    </main>
</template>

<script>
    import UserService from '../../services/UserService';
    import UserWalletService from '../../services/UserWalletService';
    import walletFormatMixin from '../../utils/walletFormatMixin';
    import WalletBalanceSection from './components/WalletBalanceSection.vue';
    import WalletHistorySection from './components/WalletHistorySection.vue';
    import WalletReceiveModal from './components/WalletReceiveModal.vue';
    import WalletStakeModal from './components/WalletStakeModal.vue';
    import WalletStakeNoticeModal from './components/WalletStakeNoticeModal.vue';
    import WalletTransferModal from './components/WalletTransferModal.vue';
    import WalletUnstakeSection from './components/WalletUnstakeSection.vue';
    import {CURRENCIES} from './constants/currencies';

    export default {
        name: 'UserWalletPage',

        mixins: [walletFormatMixin],

        components: {
            WalletBalanceSection,
            WalletHistorySection,
            WalletReceiveModal,
            WalletStakeModal,
            WalletStakeNoticeModal,
            WalletUnstakeSection,
            WalletTransferModal,
        },

        data() {
            return {
                loadingWallet: false,
                walletError: false,
                walletInfo: null,
                hasStakeApplied: false,

                showReceiveModal: false,
                showStakeModal: false,
                showStakeNoticeModal: false,
                showTransferModal: false,
                transferSymbol: 'AUBE',
            };
        },

        computed: {
            walletAddress() {
                return this.walletInfo ? this.walletInfo.wallet.address : '';
            },
            availableBalance() {
                if (!this.walletInfo) {
                    return '0';
                }
                // Floor at 0 — locked SEND can exceed ledger balance; never pass a negative
                // stakeable amount into WalletStakeModal.
                return this.nonNegativeBalance(this.walletInfo.balances.aube_available);
            },
            plans() {
                return this.walletInfo ? this.walletInfo.plans : [];
            },
        },

        created() {
            this.fetchStakeApplicationStatus();
        },

        methods: {
            onActivitiesLoaded() {
                // GlobalSpinner 無し — claim/stake 完了後の history.refresh(false) が
                // activities-loaded を再発火しても、完了モーダルがスピナーで点滅しないようにする。
                // 残高は WalletBalanceSection のローカル loading で表示する。
                this.fetchWallet(false);
            },

            /**
             * spinner = false means a background refetch (called while a modal is open, or
             * behind a completion screen). In that case loadingWallet stays off: the modal
             * backdrop is semi-transparent, so swapping the balance rows for "Loading…" is
             * visible through it and looks like a second load firing right after the one the
             * user just finished. Keep showing the stale balance and swap it for the fresh
             * value once the response lands.
             */
            async fetchWallet(spinner = true) {
                this.loadingWallet = spinner;
                this.walletError = false;
                try {
                    const response = await UserWalletService.getUserWallet(spinner);
                    this.walletInfo = response.data;
                } catch (e) {
                    this.walletError = true;
                } finally {
                    this.loadingWallet = false;
                }
            },

            async fetchStakeApplicationStatus() {
                const data = (await UserService.getUserFrontendData()).data;
                this.hasStakeApplied = data.is_aube_coin_staking_applied === true;
            },

            openReceiveModal() {
                this.showReceiveModal = true;
            },

            openStakeModal() {
                if (this.hasStakeApplied) {
                    // availableBalance is only fetched at page load (created()) and after a
                    // completed stake (onStakeAccepted) — refetch here too, otherwise the modal's
                    // client-side balance check can use a stale value if the page has been open
                    // for a while or the balance changed from elsewhere.
                    this.fetchWallet(false);
                    this.showStakeModal = true;
                } else {
                    this.showStakeNoticeModal = true;
                }
            },

            openTransferModal(balanceKey) {
                const matched = Object.keys(CURRENCIES).find((symbol) => CURRENCIES[symbol].balanceKey === balanceKey);
                this.transferSymbol = matched || String(balanceKey || 'AUBE').toUpperCase();
                this.showTransferModal = true;
            },

            onTransferClosed(shouldRefresh) {
                this.showTransferModal = false;
                if (shouldRefresh) {
                    this.fetchWallet(false);
                    this.$refs.historySection.refresh(false);
                }
            },

            onStakeAccepted() {
                // stake accepted 画面が GlobalSpinner に隠れないよう、背景更新はスピナー無しで実行
                this.fetchWallet(false);
                this.$refs.historySection.refresh(false);
            },

            onClaimed() {
                // unstake/報酬獲得 完了画面が GlobalSpinner に隠れないよう、背景更新はスピナー無しで実行
                this.fetchWallet(false);
                this.$refs.historySection.refresh(false);
            },

            onHistoryTabChange() {
                // Claim Reward が processing 中のときだけ claimable を再取得（タブ切替で完了状態を反映）
                if (this.$refs.unstakeSection) {
                    this.$refs.unstakeSection.refreshIfProcessingClaim();
                }
            },
        },
    };
</script>
